// src/components/parent-dashboard/views/AttendanceDetailView.js
import React from "react";
import { AlertCircle, ArrowLeft, Calendar, CheckCircle, X } from "lucide-react";
import { formatSimpleDate, getAttendanceRateClass, getAttendanceRateBgClass } from "../utils/formatters";

/**
 * Attendance Detail View Component
 * Shows detailed attendance information for a selected class
 */
const AttendanceDetailView = ({
    selectedChild,
    selectedClass,
    selectedClassAttendance,
    selectedChildAttendance,
    handleBack,
    loading,
    error
}) => {
    return (
        <div className="min-h-screen bg-[#F7FAFF] font-[poppins]">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                {/* Header with navigation */}
                <div className="mb-6">
                    <button
                        onClick={handleBack}
                        className="inline-flex items-center text-sm font-medium text-[#D67C2C] hover:text-[#1a5b94]"
                    >
                        <ArrowLeft size={16} className="mr-1"/>
                        Back to {selectedChild.name}'s Details
                    </button>

                    <div className="mt-3">
                        <h1 className="text-2xl font-bold text-[#D67C2C]">Attendance
                            for {selectedClass.name}</h1>
                        <p className="mt-1 text-[#2173B7]">
                            {selectedClass.description || "No description"} •
                            Teacher: {selectedClass.teacher?.name || "No teacher assigned"}
                        </p>
                    </div>
                </div>

                {/* Error message */}
                {error && (
                    <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded-md">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <AlertCircle className="h-5 w-5 text-red-500"/>
                            </div>
                            <div className="ml-3">
                                <p className="text-sm text-red-700">{error}</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Loading state */}
                {loading && (
                    <div className="flex justify-center items-center py-12">
                        <div
                            className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#D67C2C]"></div>
                    </div>
                )}

                {/* Content sections */}
                {!loading && (
                    <div className="space-y-6">
                        {/* Attendance Summary */}
                        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                            <div className="px-4 py-5 sm:px-6 bg-[#FFF8F4] border-b-2 border-[#F7D1B6]">
                                <h2 className="text-lg leading-6 font-medium text-[#D67C2C]">Attendance
                                    Summary</h2>
                            </div>
                            <div className="px-4 py-5 sm:p-6">
                                {!selectedClassAttendance ? (
                                    <div className="text-center py-6">
                                        <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4"/>
                                        <p className="text-gray-500">No attendance records available for this
                                            class yet</p>
                                    </div>
                                ) : (
                                    <div className="space-y-6">
                                        <div>
                                            <h3 className="text-base font-medium text-[#D67C2C]">Overall
                                                Attendance Rate</h3>
                                            <div className="mt-2">
                                                <div className="flex items-center">
                                                    <div className="w-full bg-gray-200 rounded-full h-2.5 mr-2">
                                                        <div
                                                            className={`h-2.5 rounded-full ${getAttendanceRateBgClass(selectedClassAttendance.attendance_rate)}`}
                                                            style={{width: `${selectedClassAttendance.attendance_rate}%`}}
                                                        ></div>
                                                    </div>
                                                    <span
                                                        className={`text-sm font-medium ${getAttendanceRateClass(selectedClassAttendance.attendance_rate)}`}>
                                                        {selectedClassAttendance.attendance_rate}%
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-3 gap-4">
                                            <div className="bg-blue-50 p-4 rounded-lg">
                                                <div className="flex flex-col items-center">
                                                    <Calendar className="h-6 w-6 text-blue-600 mb-2"/>
                                                    <span className="text-xs font-medium text-blue-800">Total Days</span>
                                                    <span
                                                        className="text-xl font-bold text-blue-900">{selectedClassAttendance.total_days}</span>
                                                </div>
                                            </div>

                                            <div className="bg-green-50 p-4 rounded-lg">
                                                <div className="flex flex-col items-center">
                                                    <CheckCircle className="h-6 w-6 text-green-600 mb-2"/>
                                                    <span
                                                        className="text-xs font-medium text-green-800">Present</span>
                                                    <span
                                                        className="text-xl font-bold text-green-900">{selectedClassAttendance.present_days}</span>
                                                </div>
                                            </div>

                                            <div className="bg-red-50 p-4 rounded-lg">
                                                <div className="flex flex-col items-center">
                                                    <X className="h-6 w-6 text-red-600 mb-2"/>
                                                    <span
                                                        className="text-xs font-medium text-red-800">Absent</span>
                                                    <span
                                                        className="text-xl font-bold text-red-900">{selectedClassAttendance.absent_days}</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div>
                                            <h3 className="text-base font-medium text-[#D67C2C] mb-2">Attendance
                                                Status</h3>
                                            <div className="space-y-2">
                                                <div className="flex justify-between items-center">
                                                    <div className="flex items-center">
                                                        <div
                                                            className="h-3 w-3 bg-green-500 rounded-full mr-2"></div>
                                                        <span className="text-sm text-gray-600">Present</span>
                                                    </div>
                                                    <div className="flex items-center">
                                                        <span className="text-sm font-medium text-gray-900">
                                                          {selectedClassAttendance.present_days} days
                                                        </span>
                                                        <span className="ml-2 text-xs text-gray-500">
                                                          ({Math.round((selectedClassAttendance.present_days / selectedClassAttendance.total_days) * 100) || 0}%)
                                                        </span>
                                                    </div>
                                                </div>

                                                <div className="flex justify-between items-center">
                                                    <div className="flex items-center">
                                                        <div
                                                            className="h-3 w-3 bg-red-500 rounded-full mr-2"></div>
                                                        <span className="text-sm text-gray-600">Absent</span>
                                                    </div>
                                                    <div className="flex items-center">
                                                        <span className="text-sm font-medium text-gray-900">
                                                          {selectedClassAttendance.absent_days} days
                                                        </span>
                                                        <span className="ml-2 text-xs text-gray-500">
                                                          ({Math.round((selectedClassAttendance.absent_days / selectedClassAttendance.total_days) * 100) || 0}%)
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Attendance Records */}
                        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                            <div className="px-4 py-5 sm:px-6 bg-[#FFF8F4] border-b-2 border-[#F7D1B6]">
                                <h2 className="text-lg leading-6 font-medium text-[#D67C2C]">Attendance
                                    Records</h2>
                            </div>
                            <div>
                                {selectedChildAttendance.length === 0 ? (
                                    <div className="text-center py-8">
                                        <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4"/>
                                        <h3 className="text-lg font-medium text-gray-900 mb-2">No attendance
                                            records</h3>
                                        <p className="text-gray-500">
                                            There are no attendance records available for this class yet.
                                        </p>
                                    </div>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-[#FFF8F4]">
                                            <tr>
                                                <th scope="col"
                                                    className="px-6 py-3 text-left text-xs font-medium text-[#2173B7] uppercase tracking-wider">
                                                    Date
                                                </th>
                                                <th scope="col"
                                                    className="px-6 py-3 text-left text-xs font-medium text-[#2173B7] uppercase tracking-wider">
                                                    Status
                                                </th>
                                                <th scope="col"
                                                    className="px-6 py-3 text-left text-xs font-medium text-[#2173B7] uppercase tracking-wider">
                                                    Day of Week
                                                </th>
                                            </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                            {selectedChildAttendance
                                                .filter(record => true) // This would be where you filter by class if needed
                                                .sort((a, b) => new Date(b.date) - new Date(a.date))
                                                .map((record, index) => (
                                                    <tr key={index}
                                                        className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                            {formatSimpleDate(record.date)}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            {record.present ? (
                                                                <span
                                                                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                                    <CheckCircle size={14} className="mr-1"/>
                                                                    Present
                                                                </span>
                                                            ) : (
                                                                <span
                                                                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                                                    <X size={14} className="mr-1"/>
                                                                    Absent
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                            {new Date(record.date).toLocaleDateString(undefined, {weekday: 'long'})}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AttendanceDetailView;